import React from "react";
import { inject, observer } from "mobx-react";
import { withStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import Slider from "@material-ui/core/Slider";
import Tooltip from "@material-ui/core/Tooltip";
import {
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeOff,
  ClosedCaption,
  ClosedCaptionOutlined,
  SkipNext
} from "@material-ui/icons";

const styles = theme => ({
  videoContainer: {
    position: "relative",
    width: "100%",
    display: "inline-block",
    pointerEvents: "auto",
    "&:hover $customControls": {
      opacity: 1,
      visibility: "visible"
    }
  },
  video: {
    width: "100%",
    height: "auto",
    display: "block",
    maxWidth: "100%"
  },
  customControls: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
    padding: "0.5rem 1.5rem",
    opacity: 0,
    visibility: "hidden",
    transition: "opacity 0.3s ease, visibility 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    zIndex: 2,
    pointerEvents: "auto"
  },
  playButton: {
    color: "white",
    padding: theme.spacing(0.5)
  },
  timeDisplay: {
    color: "white",
    fontSize: "0.75rem",
    fontFamily: theme.typography.fontFamily,
    minWidth: "60px",
    lineHeight: 1
  },
  progressSliderContainer: {
    display: "flex",
    alignItems: "center",
    flex: 1,
    gap: "1rem"
  },
  progressSlider: {
    flex: 1,
    height: 4,
    "& .MuiSlider-thumb": {
      width: 12,
      height: 12,
      backgroundColor: "white",
      "&:focus, &:hover, &:active": {
        boxShadow: `0px 0px 0px 8px ${theme.palette.primary.main}1a`
      }
    }
  },
  volumeContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5)
  },
  volumeSlider: {
    width: 80,
    height: 4,
    "& .MuiSlider-thumb": {
      width: 12,
      height: 12,
      backgroundColor: "white",
      "&:focus, &:hover, &:active": {
        boxShadow: `0px 0px 0px 8px ${theme.palette.primary.main}1a`
      }
    }
  },
  volumeButton: {
    color: "white",
    padding: theme.spacing(0.5)
  },
  fullscreenButton: {
    color: "white",
    padding: theme.spacing(0.5)
  },
  ccButton: {
    color: "white",
    padding: theme.spacing(0.5)
  },
  liveButton: {
    color: "white",
    padding: theme.spacing(0.5),
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "0.75rem",
    textTransform: "uppercase",
    fontWeight: 600
  }
});

class HLSVideo extends React.Component {
  componentWillUnmount() {
    const { hlsPlayerStore } = this.props;
    hlsPlayerStore.clearVideoEventListeners();
    hlsPlayerStore.reset();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { src, startInSeconds, hlsPlayerStore } = this.props;
    const prevSrc = prevProps.src;
    const prevStart = prevProps.startInSeconds;
    hlsPlayerStore.updateHlsPlayer(prevSrc, src, prevStart, startInSeconds);
  }

  componentDidMount() {
    const {
      src,
      assetSource,
      token,
      startInSeconds,
      hlsPlayerStore
    } = this.props;
    hlsPlayerStore.setupVideoEventListeners();
    hlsPlayerStore.initializeHlsPlayer(src, assetSource, token, startInSeconds);
  }

  formatTime = seconds => {
    if (!seconds || isNaN(seconds)) return "00:00:00";

    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    return `${hours.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  handleProgressChange = (event, newValue) => {
    const { hlsPlayerStore } = this.props;
    hlsPlayerStore.handleSliderTimeUpdate(newValue);
  };

  handleVolumeChange = (event, newValue) => {
    const { hlsPlayerStore } = this.props;
    hlsPlayerStore.handleVolumeChange(newValue);
  };

  render() {
    const { hlsPlayerStore, classes } = this.props;

    const {
      isPlaying,
      currentTime,
      duration,
      isMuted,
      volume,
      ccEnabled,
      hasCCTrack,
      isLiveFeed
    } = hlsPlayerStore;

    return (
      <div>
        <div className={classes.videoContainer}>
          <video
            ref={playerRef => {
              hlsPlayerStore.playerRef = playerRef;
              // Work around to SPORTY-255 Bug:
              // Users switching tabs were seeing the video and audio pause
              // to fix this we removed the pause event handler, but that raised a
              // new problem the video player would pause in an attempt to save resources due to
              // it being muted to resolve this I gave the video a player an arbitrary low volume that
              // should NOT be perceivable to the user, but will satisfy the browsers need to the player
              // to be "making" noise.
              if (playerRef) {
                playerRef.volume = 0.0000001;
              }
            }}
            className={classes.video}
          />

          <div className={classes.customControls}>
            <Tooltip title={isPlaying ? "Pause" : "Play"}>
              <IconButton
                className={classes.playButton}
                onClick={() => hlsPlayerStore.toggleIsPlaying()}
              >
                {isPlaying ? <Pause /> : <PlayArrow />}
              </IconButton>
            </Tooltip>
            <div className={classes.progressSliderContainer}>
              <span className={classes.timeDisplay}>
                {this.formatTime(currentTime)} / {this.formatTime(duration)}
              </span>

              <Slider
                className={classes.progressSlider}
                value={currentTime}
                max={duration}
                onChange={this.handleProgressChange}
                aria-labelledby="video-progress-slider"
              />
            </div>
            <div className={classes.volumeContainer}>
              <Tooltip title={isMuted ? "Unmute" : "Mute"}>
                <IconButton
                  className={classes.volumeButton}
                  onClick={hlsPlayerStore.toggleMute}
                >
                  {isMuted ? <VolumeOff /> : <VolumeUp />}
                </IconButton>
              </Tooltip>

              <Slider
                className={classes.volumeSlider}
                value={isMuted ? 0 : volume}
                max={100}
                onChange={this.handleVolumeChange}
                aria-labelledby="volume-slider"
              />
            </div>
            {hasCCTrack && (
              <Tooltip title="Toggle CC">
                <IconButton
                  className={classes.ccButton}
                  onClick={hlsPlayerStore.toggleCC}
                >
                  {ccEnabled ? <ClosedCaption /> : <ClosedCaptionOutlined />}
                </IconButton>
              </Tooltip>
            )}
            {isLiveFeed && (
              <Tooltip title="Jump to live">
                <IconButton
                  className={classes.liveButton}
                  onClick={() => hlsPlayerStore.jumpToLive()}
                >
                  <span>LIVE</span>
                  <SkipNext />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </div>
        <audio
          ref={audioPlayerRef =>
            (hlsPlayerStore.audioPlayerRef = audioPlayerRef)
          }
          width="100%"
          style={{ display: "none" }}
        />
      </div>
    );
  }
}

export default inject("hlsPlayerStore")(withStyles(styles)(observer(HLSVideo)));



// WEBPACK FOOTER //
// ./src/components/HLSVideo.js